import { useState, useEffect, useCallback, useRef } from 'react';
import { customerAPI } from '../services/api';
import { buildSydneyPipelineUpdate } from '../utils/acquisitionAutomation';

/**
 * Custom hook for managing customers
 * @param {Object} user - Current/legacy user object
 * @returns {Object} Customers state and methods
 */
export const useCustomers = (user) => {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const isInitialFetchRef = useRef(true);
  const automationInFlightRef = useRef(false);

  // The current login flow stores an authenticated dealer session rather than always
  // populating the legacy `user` object. Treat either form as a valid session so a
  // browser refresh still reloads customers from the backend.
  const storedDealer = localStorage.getItem('dealer') || localStorage.getItem('vincentrix_dealer');
  const storedToken = localStorage.getItem('token') || localStorage.getItem('vincentrix_token');
  const hasAuthenticatedSession = Boolean(user || (storedDealer && storedToken));

  const fetchCustomers = useCallback(async () => {
    if (!hasAuthenticatedSession) {
      setLoading(false);
      return [];
    }

    if (isInitialFetchRef.current) {
      setLoading(true);
    }
    setError(null);

    let fetchedCustomers = [];

    try {
      const data = await customerAPI.getAll();
      if (data.success) {
        fetchedCustomers = data.customers || [];
        setCustomers(fetchedCustomers);
      }
    } catch (err) {
      setError(err.message);
      console.error('Error fetching customers:', err);
    } finally {
      if (isInitialFetchRef.current) {
        setLoading(false);
        isInitialFetchRef.current = false;
      }
    }

    return fetchedCustomers;
  }, [hasAuthenticatedSession]);

  useEffect(() => {
    fetchCustomers();
  }, [fetchCustomers]);

  // Keep acquisition opportunities fresh while a manager is actively using VinCentrix.
  // This lets inbound Twilio messages recorded by the backend flow into the pipeline
  // without requiring a manual browser refresh.
  useEffect(() => {
    if (!hasAuthenticatedSession) return undefined;

    const refreshIfVisible = () => {
      if (document.visibilityState === 'visible') {
        fetchCustomers();
      }
    };

    const intervalId = window.setInterval(refreshIfVisible, 30000);
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') refreshIfVisible();
    };
    const handleFocus = () => refreshIfVisible();

    document.addEventListener('visibilitychange', handleVisibility);
    window.addEventListener('focus', handleFocus);

    return () => {
      window.clearInterval(intervalId);
      document.removeEventListener('visibilitychange', handleVisibility);
      window.removeEventListener('focus', handleFocus);
    };
  }, [fetchCustomers, hasAuthenticatedSession]);

  // Sydney acquisition automation. The backend already records inbound SMS and some
  // legacy acquisition statuses. This layer converts clear customer intent into the
  // canonical VinCentrix acquisition pipeline and persists the result to MongoDB.
  // It only evaluates the latest inbound customer message once and never runs more
  // than one automation batch at a time.
  useEffect(() => {
    if (!hasAuthenticatedSession || loading || automationInFlightRef.current || customers.length === 0) {
      return;
    }

    const pending = customers
      .map(customer => ({ customer, updates: buildSydneyPipelineUpdate(customer) }))
      .filter(item => item.updates)
      .slice(0, 12);

    if (pending.length === 0) return;

    let cancelled = false;
    automationInFlightRef.current = true;

    const runAutomation = async () => {
      const results = await Promise.allSettled(
        pending.map(async ({ customer, updates }) => {
          const data = await customerAPI.update(customer.id, updates);
          return data.customer || { ...customer, ...updates };
        })
      );

      if (!cancelled) {
        const savedById = new Map();
        results.forEach(result => {
          if (result.status === 'fulfilled' && result.value?.id !== undefined) {
            savedById.set(result.value.id, result.value);
          }
        });

        if (savedById.size > 0) {
          setCustomers(prev => prev.map(customer => savedById.get(customer.id) || customer));
        }

        results.forEach(result => {
          if (result.status === 'rejected') {
            console.error('Sydney pipeline automation error:', result.reason);
          }
        });
      }

      automationInFlightRef.current = false;
    };

    runAutomation();

    return () => {
      cancelled = true;
    };
  }, [customers, hasAuthenticatedSession, loading]);

  /**
   * Add a new customer
   * @param {Object} customerData - Customer data object
   * @returns {Promise<Object>} Created customer object
   */
  const addCustomer = async (customerData) => {
    try {
      const data = await customerAPI.create(customerData);
      if (data.customer) {
        setCustomers(prev => [...prev, data.customer]);
        return data.customer;
      }
      return data;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  /**
   * Update an existing customer
   * @param {string} id - Customer ID
   * @param {Object} customerData - Updated customer data
   * @returns {Promise<Object>} Updated customer object
   */
  const updateCustomer = async (id, customerData) => {
    try {
      const data = await customerAPI.update(id, customerData);
      if (data.customer) {
        setCustomers(prev => prev.map(c => c.id === id ? data.customer : c));
        return data.customer;
      }
      return data;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  /**
   * Delete a customer
   * @param {string} id - Customer ID
   * @returns {Promise<void>}
   */
  const deleteCustomer = async (id) => {
    try {
      await customerAPI.delete(id);
      setCustomers(prev => prev.filter(c => c.id !== id));
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  return {
    customers,
    setCustomers,
    loading,
    error,
    fetchCustomers,
    addCustomer,
    updateCustomer,
    deleteCustomer
  };
};
